import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { RentalsAnalyticsComponent } from '../../../shared/components/analytics/rentals-analytics/rentals-analytics.component';
import { VehiclesAnalyticsComponent } from '../../../shared/components/analytics/vehicles-analytics/vehicles-analytics.component';
import { WalletAnalyticsComponent } from '../../../shared/components/analytics/wallet-analytics/wallet-analytics.component';
import { OwnerAnalyticsComponent } from '../owner-analytics/owner-analytics.component';
import { rsql } from '../../../shared/helpers/rsql';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { AuthService } from '../../../shared/services/auth.service';
import { InvoicesService } from '../../../shared/services/invoices.service';
import { RentalsService } from '../../../shared/services/rentals.service';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import { WalletsService } from '../../../shared/services/wallets.service';
import { INVOICE_STATUS_META, Invoice } from '../../../shared/types/invoice.types';
import { RENTAL_STATUS_META, Rental } from '../../../shared/types/rental.types';
import { WalletSnapshotResponse } from '../../../shared/types/wallet.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { LocaleService } from '../../../shared/services/locale.service';

/** Vue agrégée, prête à afficher. */
interface OwnerDashboardVM {
  wallet: WalletSnapshotResponse;
  fleetTotal: number;
  fleetAvailable: number;
  fleetUnderReview: number;
  activeRentalsCount: number;
  activeRentals: Rental[];
  overdueCount: number;
  overdueInvoices: Invoice[];
}

/** Véhicules « en attente de validation » (les 2 états de revue). */
const REVIEW_STATUSES = ['UNDER_REVIEW', 'PRE_UNDER_REVIEW'] as const;
/** Locations « actives » = confirmées (à venir) ou en cours. */
const ACTIVE_RENTAL_STATUSES = ['CONFIRMED', 'STARTED'] as const;

/**
 * Tableau de bord owner (#40) — accueil `/` après login. Remplace le mock
 * TailAdmin `EcommerceComponent`.
 *
 * ⚠️ Pas d'endpoint d'agrégation dédié côté back (contrairement au dashboard
 * admin `/operator/api/admin/dashboard`) : on **compose** en parallèle depuis
 * les endpoints **self-scoped** existants (`wallet/accounts/me`,
 * `vehicles/me`, `rentals/me`, `invoices/me/overdue`). Compteurs exacts via
 * `totalItems` (RSQL côté serveur) ; la répartition de flotte est dérivée de
 * la 1ʳᵉ page (≤ 100), suffisant pour un coup d'œil.
 */
@Component({
  selector: 'app-owner-dashboard',
  imports: [DatePipe, DecimalPipe, RouterLink, DataStateComponent, OwnerAnalyticsComponent, RentalsAnalyticsComponent, VehiclesAnalyticsComponent, WalletAnalyticsComponent, TPipe, LocalizedDatePipe],
  templateUrl: './owner-dashboard.component.html',
})
export class OwnerDashboardComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly wallets = inject(WalletsService);
  private readonly vehicles = inject(VehiclesService);
  private readonly rentals = inject(RentalsService);
  private readonly invoices = inject(InvoicesService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly rentalMeta = RENTAL_STATUS_META;
  readonly invoiceMeta = INVOICE_STATUS_META;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly vm = signal<OwnerDashboardVM | null>(null);

  /** Prénom pour l'accueil (fallback displayName). */
  readonly greetingName = computed(() => {
    const u = this.auth.user();
    return u?.firstName || u?.displayName || '';
  });

  /** Bandeaux d'alerte, dans l'ordre d'urgence. Vide = tout est au vert. */
  readonly alerts = computed<{ tone: 'error' | 'warning'; text: string; link: string }[]>(() => {
    const v = this.vm();
    if (!v) return [];
    const out: { tone: 'error' | 'warning'; text: string; link: string }[] = [];
    if (v.wallet.debt > 0) {
      out.push({
        tone: 'error',
        text: this.locale.t('dashboard.alertDebt', { amount: this.fmt(v.wallet.debt) }),
        link: '/my-wallet',
      });
    }
    if (v.overdueCount > 0) {
      out.push({
        tone: 'error',
        text: this.locale.t('dashboard.alertOverdue', { count: v.overdueCount }),
        link: '/my-invoices',
      });
    }
    if (v.fleetUnderReview > 0) {
      out.push({
        tone: 'warning',
        text: this.locale.t('dashboard.alertReview', { count: v.fleetUnderReview }),
        link: '/my-vehicles',
      });
    }
    return out;
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      wallet: this.wallets.getMine('OWNER'),
      // Flotte : 1 page large → total exact (totalItems) + répartition dérivée.
      vehicles: this.vehicles.listMine({ page: 1, size: 100 }),
      // Locations actives : compteur exact (totalItems) + 5 à afficher.
      activeRentals: this.rentals.listMine({
        page: 1,
        size: 5,
        search: rsql.in('rentalStatusEnum', [...ACTIVE_RENTAL_STATUSES]) ?? undefined,
      }),
      // Factures en retard : endpoint dédié → compteur exact + 5 à afficher.
      overdue: this.invoices.listMineOverdue({ page: 1, size: 5 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const fleet = res.vehicles.data ?? [];
          this.vm.set({
            wallet: res.wallet,
            fleetTotal: res.vehicles.totalItems ?? fleet.length,
            fleetAvailable: fleet.filter((v) => v.status === 'AVAILABLE').length,
            fleetUnderReview: fleet.filter((v) =>
              (REVIEW_STATUSES as readonly string[]).includes(v.status),
            ).length,
            activeRentalsCount: res.activeRentals.totalItems ?? 0,
            activeRentals: res.activeRentals.data ?? [],
            overdueCount: res.overdue.totalItems ?? 0,
            overdueInvoices: res.overdue.data ?? [],
          });
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.error.set(toErrorMessage(err, 'errors.dashboardLoadFailed'));
        },
      });
  }

  /** Nom véhicule d'une location, pour les listes (cf. rental-detail). */
  rentalVehicle(r: Rental): string {
    const v = r.vehicle;
    if (!v) return this.locale.t('common.vehicle');
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || v.licencePlate || this.locale.t('label.vehicleNum', { id: v.id });
  }

  private fmt(n: number): string {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n);
  }
}
